// Merkezi API istemcisi (mobildeki ApiService + AuthInterceptor +
// LanguageInterceptor'ın web karşılığı).
//
// - baseURL: REACT_APP_API_URL veya prod (https://api.utkanesim.com)
// - Her isteğe Bearer access token (/auth/refresh hariç), Accept-Language ve
//   Platform: web başlıkları eklenir.
// - 401 → tek-uçuşlu (single-flight) refresh → orijinal istek yeni token'la
//   bir kez tekrarlanır. Eşzamanlı 401'ler aynı refresh'i bekler; refresh
//   token rotation olduğu için expiry başına yalnızca BİR /auth/refresh atılır.

import axios from 'axios';
import {
  getAccessToken,
  getRefreshToken,
  saveTokens,
} from './tokenStorage';
import { forceLogout } from './sessionManager';

const getBaseUrl = () =>
  process.env.REACT_APP_API_URL || 'https://api.utkanesim.com';

const getLanguage = () => {
  const stored = localStorage.getItem('i18nextLng') || 'tr';
  // 'en-US' gibi değerleri 2 harfli koda indir.
  return stored.split('-')[0];
};

const api = axios.create({
  baseURL: getBaseUrl(),
  timeout: 50000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    Platform: 'web',
  },
});

// Refresh isteği için interceptor'sız ayrı instance: refresh çağrısı bu
// interceptor'lardan geçmez, eski bearer taşımaz ve sonsuz döngü yaratmaz.
const refreshClient = axios.create({
  baseURL: getBaseUrl(),
  timeout: 50000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// --- Request interceptor: dil + bearer token ---
api.interceptors.request.use((config) => {
  config.headers['Accept-Language'] = getLanguage();

  const isRefreshCall = config.url && config.url.includes('/auth/refresh');
  if (!isRefreshCall) {
    const accessToken = getAccessToken();
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
  }
  return config;
});

// --- Single-flight refresh ---
let refreshPromise = null;

const doRefresh = async () => {
  try {
    const refreshToken = getRefreshToken();
    if (!refreshToken) return null;

    const response = await refreshClient.post('/auth/refresh', {
      refreshToken,
    });

    const data = response.data?.data;
    const newAccessToken = data?.accessToken;
    const newRefreshToken = data?.refreshToken;
    if (!newAccessToken || !newRefreshToken) return null;

    saveTokens({ access: newAccessToken, refresh: newRefreshToken });
    return newAccessToken;
  } catch (error) {
    // Geçici ağ hatasında oturumu kapatma; çağırana null yerine hata fırlat.
    if (!error.response) {
      throw error;
    }
    // 401 vb. kalıcı hata: refresh token artık geçersiz.
    return null;
  } finally {
    refreshPromise = null;
  }
};

// --- Response interceptor: 401 → refresh → retry once ---
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;

    const isRefreshCall = original?.url && original.url.includes('/auth/refresh');

    if (status !== 401 || !original || isRefreshCall) {
      return Promise.reject(error);
    }

    // İstek başına en fazla bir retry: refresh sonrası tekrar 401 gelirse
    // (ör. kullanıcı sunucuda devre dışı) döngüye girme.
    if (original._retried) {
      return Promise.reject(error);
    }

    const sentToken = original.headers?.Authorization?.replace('Bearer ', '');
    const currentToken = getAccessToken();

    let newToken;
    if (currentToken && currentToken !== sentToken) {
      // Bu istek havadayken başka bir istek refresh'i zaten tamamlamış.
      // Tek kullanımlık refresh token'ı tekrar yakmadan güncel token'la dene.
      newToken = currentToken;
    } else {
      if (!getRefreshToken()) {
        // Oturum yok (guest akışı veya logout sonrası).
        return Promise.reject(error);
      }
      if (!refreshPromise) {
        refreshPromise = doRefresh();
      }
      try {
        newToken = await refreshPromise;
      } catch (networkErr) {
        // Refresh sırasında geçici ağ hatası: oturumu sonlandırma.
        return Promise.reject(error);
      }
      if (!newToken) {
        // Refresh token geçersiz/süresi dolmuş: oturumu tek seferde kapat.
        forceLogout();
        return Promise.reject(error);
      }
    }

    original._retried = true;
    original.headers.Authorization = `Bearer ${newToken}`;
    return api(original);
  }
);

export default api;
